import { DestroyRef, inject, Injectable, QueryList } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  combineLatest,
  EMPTY,
  merge,
  Observable,
  of,
  pipe,
  Subject,
  UnaryFunction
} from 'rxjs';
import {
  delay,
  first,
  map,
  skipWhile,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { FeatureKeys } from 'src/app/feature-flag/feature-flag.model';
import { FeatureFlagService } from 'src/app/feature-flag/feature-flag.service';
import { GlobalSettings } from 'src/app/global-settings/global-settings.service';
import { ScheduleStrategyKeys } from 'src/app/product-capture/create-product/event-schedules/event-schedules.const';
import { CouponScheduleComponent } from 'src/app/product-capture/create-product/payoff/schedule/coupon-schedule/coupon-schedule.component';
import { ScheduleComponent } from 'src/app/product-capture/create-product/payoff/schedule/schedule.component';
import { ClientType } from 'src/app/product-capture/model/consts/client-type.const';
import { DAY_COUNT_KEYS } from 'src/app/product-capture/model/consts/day-count.const';
import { LEGAL_FORM_KEYS } from 'src/app/product-capture/model/consts/legal-form.const';
import { LISTING_STATUS_KEYS } from 'src/app/product-capture/model/consts/listing-status.const';
import { PERFORMANCE_TYPE_KEYS } from 'src/app/product-capture/model/consts/performance-type.const';
import { COUPON_TYPE } from 'src/app/product-capture/model/enums/coupon-type.enum';
import {
  LegalEnum,
  LegalFormEnum,
  ProductCaptureGroupsEnum,
  ProductTypeEnum,
  ScheduleType,
} from 'src/app/product-capture/model/enums/product-capture.enums';
import { ROLL_CONVENTION_ENUM } from 'src/app/product-capture/model/enums/roll-convention.enum';
import { SCHEDULE_TYPE_ENUM } from 'src/app/product-capture/model/enums/schedule-type.enum';
import {
  IssuerEntity,
  LegalProgrammes,
} from 'src/app/product-capture/model/interfaces/issuer-entity.interface';
import { ClientDomain } from 'src/app/product-capture/product-capture-details/overview/documents-tab/request-modal/request-modal.consts';
import { STATIC_DATA_TYPE } from 'src/app/static-data/models/consts/static-data.enum';
import { ListingVenue } from 'src/app/static-data/models/interfaces/static-data';
import { StaticDataService } from 'src/app/static-data/services/static-data.service';
import { ClientUtils } from 'src/app/utils/client.utils';
import { DateUtils } from 'src/app/utils/date.utils';
import { LegalSubscriptionType } from '../../mockups/consts/legal-subscription-type.const';
import {
  CREDIT_PROVISION_ACCRUED_INTEREST,
  CREDIT_PROVISION_CASH_SETTLEMENT_QUOTATION_METHOD,
  CREDIT_PROVISION_CASH_SETTLEMENT_VALUATION_METHOD,
  CREDIT_PROVISION_DC_DETERMINATIONS,
  CREDIT_PROVISION_EXCLUDED_OBLIGATIONS,
  CREDIT_PROVISION_EXTENSION_PERIOD_INTEREST,
  CREDIT_PROVISION_LOSS_AT_MATURITY,
  CREDIT_PROVISION_PASS_THROUGH_SECURITIES,
  CREDIT_PROVISION_PHYSICAL_SETTLEMENT_MATRIX,
  CREDIT_PROVISION_UNWIND_COSTS,
  EVENT_SCHEDULE_CALL_NOTICE_LAG,
  LEGAL_FORM_LEGAL_GOVERNING_LAWS,
  LEGAL_FORM_LEGAL_PROGRAMME,
  LEGAL_FORM_LEGAL_SECURED_ISSUANCE,
  PAYOFF_CALLABILITY_AMOUNT,
  PAYOFF_CALLABILITY_AMOUNT_TYPE,
  PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
  PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
  PAYOFF_COUPON_CONVERSION,
  PAYOFF_COUPON_DAY_COUNT,
  PAYOFF_COUPON_FREQUENCY,
  PAYOFF_COUPON_REFERENCE_RATE,
  PAYOFF_COUPON_TYPE,
  PAYOFF_PARTICIPATION,
  PAYOFF_PARTICIPATION_BASE_TYPE,
  PAYOFF_PARTICIPATION_CAPITAL_BASE,
  PAYOFF_PARTICIPATION_CAPITAL_BASE_CAPITAL_BASE,
  PAYOFF_PARTICIPATION_CAPITAL_BASE_TYPE,
  PAYOFF_PARTICIPATION_DOWNSIDE_TYPE,
  PAYOFF_PARTICIPATION_UPSIDE_TYPE,
  SALES_SECONDARY_MARKET_QUOTE_TYPE,
  SALES_SECONDARY_MARKET_REQUIRED,
  SUMMARY_ACCRUAL_ROLL_CONVENTION,
  SUMMARY_FREQUENCY,
  SUMMARY_PERFORMANCE_TYPE,
  SUMMARY_REFERENCE_ENTITIES,
  SUMMARY_SETTLEMENT_ROLL_CONVENTION,
  SUMMARY_SETTLEMENT_TYPE,
  SUMMARY_UNDERLYING_ASSETS_GROUP,
} from '../../mockups/consts/product-capture-field-selectors.consts';
import { FCFieldEffect } from '../form-config.interface';

const FIXED_CAPITAL_BASE_TYPE = {
  formControlNames: [PAYOFF_PARTICIPATION_CAPITAL_BASE_TYPE],
  selectValues: ['FIXED'],
};
const ONE_HUNDRED_PERCENT_CAPITAL_BASE = {
  formControlNames: [PAYOFF_PARTICIPATION_CAPITAL_BASE_CAPITAL_BASE],
  selectValues: [
    {
      minValue: null,
      maxValue: null,
      value: '100%',
      indicativeValue: null,
    },
  ],
};

export const DEFAULT_AND_HIDE_CAPITAL_BASE_VALUES: FCFieldEffect[] = [
  {
    ...FIXED_CAPITAL_BASE_TYPE,
    hidden: true,
  },
  {
    ...ONE_HUNDRED_PERCENT_CAPITAL_BASE,
    hidden: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_CAPITAL_BASE],
    hidden: true,
  },
];

export const DEFAULT_AND_SHOW_CAPITAL_BASE_VALUES: FCFieldEffect[] = [
  {
    ...FIXED_CAPITAL_BASE_TYPE,
    hidden: false,
  },
  {
    ...ONE_HUNDRED_PERCENT_CAPITAL_BASE,
    hidden: false,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_CAPITAL_BASE],
    hidden: false,
  },
];

export const DEFAULT_CLN_SETTINGS: {
  id: ProductTypeEnum;
  effects: FCFieldEffect[];
} = {
  id: ProductTypeEnum.CLN,
  effects: [
    {
      formControlNames: [
        PAYOFF_COUPON_REFERENCE_RATE,
        PAYOFF_COUPON_CONVERSION,
        SUMMARY_UNDERLYING_ASSETS_GROUP,
        PAYOFF_PARTICIPATION,
      ],
      hidden: true,
    },
    {
      formControlNames: [
        SUMMARY_REFERENCE_ENTITIES,
        PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
        PAYOFF_COUPON_TYPE,
      ],
      hidden: false,
    },
    {
      formControlNames: [PAYOFF_COUPON_TYPE],
      selectValues: [COUPON_TYPE.FLOATING],
      readOnly: false,
    },
    {
      formControlNames: [
        PAYOFF_PARTICIPATION_DOWNSIDE_TYPE,
        PAYOFF_PARTICIPATION_BASE_TYPE,
        PAYOFF_PARTICIPATION_UPSIDE_TYPE,
      ],
      selectValues: ['NONE'],
      hidden: true,
    },
    {
      formControlNames: [PAYOFF_CALLABILITY_CALL_EVENT_TYPE],
      selectValues: ['NONE'],
      readOnly: false,
    },
    {
      formControlNames: [PAYOFF_CALLABILITY_AMOUNT_TYPE],
      selectValues: [null],
      readOnly: false,
    },
    {
      formControlNames: [PAYOFF_CALLABILITY_AMOUNT],
      selectValues: [null],
      readOnly: false,
    },
    ...DEFAULT_AND_SHOW_CAPITAL_BASE_VALUES,
    {
      formControlNames: [CREDIT_PROVISION_PHYSICAL_SETTLEMENT_MATRIX],
      selectValues: [true],
    },
    {
      formControlNames: [CREDIT_PROVISION_CASH_SETTLEMENT_QUOTATION_METHOD],
      selectValues: ['BID'],
    },
    {
      formControlNames: [CREDIT_PROVISION_LOSS_AT_MATURITY],
      selectValues: ['AT_MATURITY'],
    },
    {
      formControlNames: [SUMMARY_PERFORMANCE_TYPE],
      selectValues: [PERFORMANCE_TYPE_KEYS.SINGLE],
      readOnly: true,
    },
    {
      formControlNames: [
        SUMMARY_FREQUENCY,
        SUMMARY_SETTLEMENT_TYPE,
        PAYOFF_COUPON_FREQUENCY,
      ],
      readOnly: false,
    },
    {
      formControlNames: [
        SUMMARY_SETTLEMENT_ROLL_CONVENTION,
        SUMMARY_ACCRUAL_ROLL_CONVENTION,
      ],
      selectValues: [ROLL_CONVENTION_ENUM.MODIFIED_FOLLOWING],
    },
  ],
};

export const DB_CLN_SETTINGS: {
  id: ProductTypeEnum;
  effects: FCFieldEffect[];
} = {
  ...DEFAULT_CLN_SETTINGS,
  effects: [
    ...DEFAULT_CLN_SETTINGS.effects,
    {
      formControlNames: [LEGAL_FORM_LEGAL_GOVERNING_LAWS],
      selectValues: ['English'],
    },
    {
      formControlNames: [SUMMARY_SETTLEMENT_TYPE],
      selectValues: ['CASH'],
    },
    {
      formControlNames: [PAYOFF_COUPON_DAY_COUNT],
      selectValues: [DAY_COUNT_KEYS.BOND_BASIS_30_360],
    },
    {
      formControlNames: [EVENT_SCHEDULE_CALL_NOTICE_LAG],
      selectValues: ['5B'],
    },
    {
      formControlNames: [CREDIT_PROVISION_CASH_SETTLEMENT_VALUATION_METHOD],
      selectValues: ['HIGHEST'],
    },
    {
      formControlNames: [
        CREDIT_PROVISION_PASS_THROUGH_SECURITIES,
        PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
        LEGAL_FORM_LEGAL_SECURED_ISSUANCE,
        CREDIT_PROVISION_EXCLUDED_OBLIGATIONS,
      ],
      selectValues: [false],
    },
    {
      formControlNames: [CREDIT_PROVISION_ACCRUED_INTEREST],
      selectValues: ['EXCLUDE_ACCRUED_INTEREST'],
    },
    {
      formControlNames: [
        CREDIT_PROVISION_EXTENSION_PERIOD_INTEREST,
        CREDIT_PROVISION_UNWIND_COSTS,
        CREDIT_PROVISION_DC_DETERMINATIONS,
        SALES_SECONDARY_MARKET_REQUIRED,
      ],
      selectValues: [true],
    },
    {
      formControlNames: [SALES_SECONDARY_MARKET_QUOTE_TYPE],
      selectValues: ['DIRTY'],
    },
    {
      formControlNames: [PAYOFF_CALLABILITY_CALL_EVENT_TYPE],
      selectValues: ['ISSUER_CALL'],
    },
  ],
};

export interface ProductSettings {
  form: FormGroup;
  schedules?: QueryList<ScheduleComponent>;
}

export interface SettingsEvent {
  type: string;
  value?: any;
}

export abstract class CustomProductSettingsService {

  form: FormGroup;

  settings: ProductSettings;

  destroyRef = inject(DestroyRef);

  route = inject(ActivatedRoute);

  featureFlagService = inject(FeatureFlagService);

  schedules: QueryList<ScheduleComponent>;

  protected reset$ = new Subject<void>();

  applySettings(settings: ProductSettings): void {
    const isFeatureFlagEnabled = GlobalSettings.getFeatureFlag(FeatureKeys.RULES_BASED_DEFAULTING);
    const isDB = ClientUtils.isClient(ClientDomain.DeutscheBank);
    this.reset$.next();
    
    if (isFeatureFlagEnabled?.isEnabled || !isDB) {
      return;
    }
    
    this.extractSettings(settings);
    setTimeout(() => this.setDefaultSettingsForDB(settings), 100);
  }

  extractSettings(settings: ProductSettings): void {
    const { form, schedules } = settings;
    this.settings = settings;
    this.form = form;
    this.schedules = schedules;
  }

  isProductEditing(): boolean {
    const productId = this.route.snapshot.params.id;
    return productId !== 'new';
  }

  abstract setDefaultSettingsForDB(settings: ProductSettings): void;

  abstract getProductType(): ProductTypeEnum[];

  get productTypeControl(): FormControl {
    return this.form?.get('summary.productType') as FormControl;
  }

  get productTypeValue(): ProductTypeEnum {
    return this.productTypeControl?.value as ProductTypeEnum;
  }

  skipIfNotProduct(value?: any): Observable<any> {
    return this.getProductType().includes(this.productTypeValue) ? of(value) : EMPTY;
  }

  onEvent = new Subject<SettingsEvent>;
}


@Injectable()
export class CLNSettingsService extends CustomProductSettingsService {

  staticDataService = inject(StaticDataService);

  legalProgrammes: LegalProgrammes[] = [];

  /* Variables used for ETLX Listing Venue defaulting on Italian CLNs */
  private hasRemovedETLX = false; // Tracks if user manually removed ETLX
  private hasAddedETLX = false; // Tracks if ETLX was ever added automatically

  setDefaultSettingsForDB(): void {
    this.setCallNoticeLag();
    this.setLastPeriodCallable();
    this.setTargetMarketsSettings();
    this.setCouponScheduleType();
    this.setIssuerEntity();
    this.setProductIdentifiers();
    this.setAgents();
    this.setSettlementCalendars();
    this.observeLegalProgramme();
    this.observeCountriesOfOffer();
    this.observeForItalianCLNs();
  }

  getProductType(): ProductTypeEnum[] {
      return [ProductTypeEnum.CLN];
  }

  setCallNoticeLag(): void {
    const { scheduleStrategy, earlyRedemption } = (
      this.form.get('summary.schedule') as FormGroup
    )?.controls;

    if (!scheduleStrategy || !earlyRedemption) {
      return;
    }

    const callNoticeLag = earlyRedemption.get('callNoticeLag');

    scheduleStrategy.valueChanges
      .pipe(this.commonPipe(scheduleStrategy.value))
      .subscribe((strategy) => {
        if (
          strategy !== ScheduleStrategyKeys.COUPON_ONLY &&
          !callNoticeLag.touched &&
          !callNoticeLag.value
        ) {
          callNoticeLag.setValue('5B');
        }
      });
  }

  setLastPeriodCallable(): void {
    const { callEventType, lastPeriodCallable } = (
      this.form.get('payoff.callability') as FormGroup
    ).controls;

    callEventType.valueChanges
      .pipe(this.commonPipe(callEventType.value))
      .subscribe(() => {
        if (
          lastPeriodCallable.enabled &&
          !lastPeriodCallable.touched &&
          !this.isProductEditing()
        ) {
          lastPeriodCallable.setValue(false);
        }
      });
  }

  setTargetMarketsSettings(): void {
    const legalProgrammeName = this.form.get(
      LEGAL_FORM_LEGAL_PROGRAMME.join('.')
    ) as FormControl;
    const targetMarkets = this.form.get('sales.targetMarkets') as FormArray;
    const groupsObserved = new Set<FormGroup>();

    targetMarkets.valueChanges
      .pipe(
        this.commonPipe(targetMarkets.value),
        map(() => targetMarkets.length)
      )
      .subscribe(() => {
        targetMarkets.controls.forEach((group: FormGroup) => {
          if (!groupsObserved.has(group)) {
            this.observeTargetMarket(group, groupsObserved, legalProgrammeName);
          }
        });
      });
  }

  observeTargetMarket(
    row: FormGroup,
    groupsObserved: Set<FormGroup>,
    legalProgrammeNameControl: FormControl
  ): void {
    const { clientType, distributionStrategy, knowledgeAndExperience } =
      row.controls;
    groupsObserved.add(row);

    combineLatest([
      clientType.valueChanges.pipe(startWith(clientType.value)),
      legalProgrammeNameControl.valueChanges.pipe(startWith(legalProgrammeNameControl.value)),
    ])
      .pipe(
        this.commonPipe([clientType.value, legalProgrammeNameControl.value]),
        delay(0)
      )
      .subscribe(([value]: [ClientType, any]) => {
        if (!value || (this.isProductEditing() && !clientType.touched)) {
          return;
        }

        const isProfessional = value === ClientType.PROFESSIONAL;
        const isRetail = value === ClientType.RETAIL;

        let distributionStrategyValue = null;
        const secNotes = this.legalProgrammes.find(
          (programme) => programme.name === 'CLN Securities Note 2025'
        );
        const offCircular = this.legalProgrammes.find(
          (programme) => programme.name === 'CLN Offering Circular 2024'
        );

        const legalProgramme = this.form.get('legalForm.legal.legalProgramme');
        if (isRetail) {
          distributionStrategyValue = 'ADVISED';
          secNotes &&
            !legalProgramme.touched &&
            legalProgramme.value !== secNotes.id &&
            legalProgramme.setValue(secNotes.id);
        } else if (isProfessional) {
          distributionStrategyValue = 'NON_ADVISED';
          const legalForm = this.form.get('legalForm.legal.legalFormDropdown');
          const coutriesOfOffer = this.form.get(
            'legalForm.countriesOfOffer.countriesOfOfferArr'
          ) as FormArray;
          const hasGermany = coutriesOfOffer.controls.find(
            (country: FormGroup) =>
              country.get('jurisdictionsLegal').value === 'Germany'
          );

          const isOfferingCircular =
            legalForm.value !== 'CERTIFICATE' && !hasGermany;
          const legalProgrammeValueToSet = isOfferingCircular
            ? offCircular?.id
            : secNotes?.id;
          if (
            !legalProgramme.touched &&
            legalProgramme.value !== legalProgrammeValueToSet
          ) {
            legalProgramme.setValue(legalProgrammeValueToSet);
          }
        }

        const knowledgeAndExperienceValue =
          isProfessional || isRetail ? 'INFORMED' : null;
        distributionStrategy.setValue(distributionStrategyValue, {
          emitEvent: false,
        });
        knowledgeAndExperience.setValue(knowledgeAndExperienceValue, {
          emitEvent: false,
        });
      });
  }

  setCouponScheduleType(): void {
    const callEventTypeControl = this.form.get(
      'payoff.callability.callEventType'
    );
    this.schedules.changes
      .pipe(
        this.commonPipe(this.schedules),
        skipWhile(() => this.schedules.length === 0),
        switchMap(() => {
          const couponSchedule = this.schedules.find(
            (schedule) => schedule.key === ScheduleType.COUPON
          );
          return couponSchedule ? of(couponSchedule) : EMPTY;
        })
      )
      .subscribe((couponSchedule: CouponScheduleComponent) => {
        const { value } = callEventTypeControl;
        const isIssuerCall = value === 'ISSUER_CALL';

        if (
          !this.isProductEditing() &&
          !couponSchedule.scheduleType.touched &&
          isIssuerCall
        ) {
          couponSchedule.scheduleType.setValue(
            SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY
          );
        }
      });
  }

  commonPipe(startWithValue?: any): UnaryFunction<any, any> {
    let commonPipe = pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.reset$),
        takeUntil(this.form.get('summary.productType').valueChanges.pipe(switchMap(() => this.skipIfNotProduct()))),
        switchMap((value) => this.skipIfNotProduct(value))
    );

    if (arguments.length === 1) {
      commonPipe = pipe(commonPipe, startWith(startWithValue));
    }

    return commonPipe;
  }

  setIssuerEntity(): void {
    const issuerEntity = this.form.get('legalForm.legal.issuerEntityId');
    this.staticDataService
      .getData(STATIC_DATA_TYPE.ISSUERS_ENTITY)
      .pipe(first())
      .subscribe((issuers: IssuerEntity[]) => {
        const issuer = issuers.find(
          (issuer) =>
            issuer.entityName === 'Deutsche Bank AG, Frankfurt am Main'
        );
        this.legalProgrammes = issuer?.legalProgrammes || [];

        if (issuerEntity.value) {
          return;
        }

        issuer && issuerEntity.setValue(issuer.id);
      });
  }

  getListingVenueOptions(): Observable<any> {
    const listingVenueData$ = this.staticDataService
      .getData(STATIC_DATA_TYPE.LISTING_VENUES)
      .pipe(first());
    if (
      this.productTypeValue === ProductTypeEnum.CLN &&
      ClientUtils.isClient(ClientDomain.DeutscheBank)
    ) {
      return listingVenueData$.pipe(
        map((listingVenues: ListingVenue[]) =>
          listingVenues.filter(
            (venue) =>
              venue.longName !== 'XETRA' &&
              venue.longName !== 'London Stock Exchange'
          )
        )
      );
    }

    return listingVenueData$;
  }

  setProductIdentifiers(): void {
    const identifiers = this.form.get(
      'legalForm.productIdentifiers.productIdentifiersArr'
    ) as FormArray;
    if (identifiers.touched || this.isProductEditing()) {
      return;
    }

    const codes = ['Common Code', 'RIC', 'WKN', 'FIC Issuance Mandate ID'];
    codes.forEach((code: string) => {
      const existingCode = identifiers.controls.some(
        (control: FormGroup) =>
          control.get('typeProdIdentifiers').value === code
      );
      if (!existingCode) {
        identifiers.push(
          new FormGroup({
            typeProdIdentifiers: new FormControl(code),
            codeProdIdentifiers: new FormControl(),
          })
        );
      }
    });

    const firstRow = identifiers.at(0);
    if (firstRow && !firstRow.get('typeProdIdentifiers').value) {
      identifiers.removeAt(0);
    }
  }

  setAgents(): void {
    const agents = this.form.get('agents.agentsArr') as FormArray;
    if (this.isProductEditing()) {
      return;
    }

    const hasCalculationAgent = agents.controls.some(
      (control: FormGroup) =>
        control.get('id').value === 'Calculation Agent' &&
        control.get('entityName').value === 'Deutsche Bank AG, Frankfurt am Main'
    );

    const hasClearingAgent = agents.controls.some(
      (control: FormGroup) =>
        control.get('id').value === 'Clearing' &&
        control.get('entityName').value === 'Euroclear Bank S.A./N.V. Brussel'
    );

    if (!hasCalculationAgent) {
      this.addAgentRow();
      const lastRow = agents.get([agents.length - 1]);

      lastRow.patchValue({
        id: 'Calculation Agent',
        entityName: 'Deutsche Bank AG, Frankfurt am Main',
      });
    }

    if (!hasClearingAgent) {
      this.addAgentRow();
      const lastRow = agents.get([agents.length - 1]);
      lastRow.patchValue({
        id: 'Clearing',
        entityName: 'Euroclear Bank S.A./N.V. Brussel',
      });
    }

    const firstRow = agents.at(0);
    if (firstRow && !firstRow.get('id').value) {
      agents.removeAt(0);
    }
  }

  addAgentRow(): void {
    this.onEvent.next({ type: 'addAgentRow' });
  }

  setSettlementCalendars(): void {
    const settlementCalendars = this.form.get(
      'summary.settlementCalendars'
    ) as FormControl;
    if (settlementCalendars.touched || this.isProductEditing()) {
      return;
    }

    const defaultCalendars = ['London', 'Frankfurt', 'TARGET2'];
    settlementCalendars.setValue(defaultCalendars);
  }

  observeLegalProgramme(): void {
    const legalFormField = this.form.get('legalForm.legal.legalFormDropdown');
    const countriesOfOfferArr = this.form.get(
      'legalForm.countriesOfOffer.countriesOfOfferArr'
    ) as FormArray;
    const legalProgramme = this.form.get('legalForm.legal.legalProgramme');

    combineLatest([
      legalFormField.valueChanges.pipe(startWith(legalFormField.value)),
      countriesOfOfferArr.valueChanges.pipe(
        startWith(countriesOfOfferArr.value)
      ),
    ])
      .pipe(this.commonPipe())
      .subscribe(() => {
        const { clientType } = this.form.get('sales.targetMarkets').value?.[0];

        if (clientType) {
          const secNotes = this.legalProgrammes.find(
            (programme) => programme.name === 'CLN Securities Note 2025'
          );
          const offCircular = this.legalProgrammes.find(
            (programme) => programme.name === 'CLN Offering Circular 2024'
          );

          if (clientType === ClientType.RETAIL) {
            secNotes && legalProgramme.setValue(secNotes.id);
          } else if (clientType === ClientType.PROFESSIONAL) {
            const legalForm = this.form.get(
              'legalForm.legal.legalFormDropdown'
            );
            const coutriesOfOffer = this.form.get(
              'legalForm.countriesOfOffer.countriesOfOfferArr'
            ) as FormArray;
            const hasGermany = coutriesOfOffer.controls.find(
              (country: FormGroup) =>
                country.get('jurisdictionsLegal').value === 'Germany'
            );

            const isOfferingCircular =
              legalForm.value !== 'CERTIFICATE' && !hasGermany;
            const value = isOfferingCircular ? offCircular?.id : secNotes?.id;
            if (value != null) {
              // Not sure if id can be 0...
              legalProgramme.setValue(value);
            }
          }
        }
      });
  }

  observeCountriesOfOffer(): void {
    const countriesOfOfferArr = this.form.get(
      'legalForm.countriesOfOffer.countriesOfOfferArr'
    ) as FormArray;
    const groupsObserved = new Set<FormGroup>();

    countriesOfOfferArr.valueChanges
      .pipe(this.commonPipe(countriesOfOfferArr.value))
      .subscribe(() => {
        countriesOfOfferArr.controls.forEach(
          (group: FormGroup, index: number) => {
            if (!groupsObserved.has(group)) {
              groupsObserved.add(group);

              group
                .get('subscription')
                .valueChanges.pipe(this.commonPipe())
                .subscribe((hasSubscriptions) => {
                  const { jurisdictionsLegal, formOfOfferLegal } = group.value;
                  const isItaly = jurisdictionsLegal === 'Italy';
                  const isPublicOffer =
                    formOfOfferLegal === LegalSubscriptionType.PUBLIC;

              if (hasSubscriptions && isItaly && isPublicOffer) {
                this.addCountryOfOfferSubscription(index);
                this.addCountryOfOfferSubscription(index);

                setTimeout(() =>
                  this.defaultCountryOfOfferSubscription(group)
                );
              }
            });
            }
          }
        );
      });

    const { issueDate, redemptionDate } = (
      this.form.get('summary.keyDates') as FormGroup
    ).controls;
    const issueDateValueCtrl = issueDate.get('value');
    const redemptionDateValueCtrl = redemptionDate.get('value');
    merge(
      issueDateValueCtrl.valueChanges.pipe(startWith(issueDateValueCtrl.value)),
      redemptionDateValueCtrl.valueChanges.pipe(
        startWith(redemptionDateValueCtrl.value)
      )
    )
      .pipe(this.commonPipe())
      .subscribe(() => {
        countriesOfOfferArr.controls.forEach((group: FormGroup) => {
          setTimeout(() => this.defaultCountryOfOfferSubscription(group));
        });
      });
  }

  addCountryOfOfferSubscription(index: number): void {
    this.onEvent.next({
      type: 'addCountryOfOfferSubscription',
      value: index,
    });
  }

  defaultCountryOfOfferSubscription(group: FormGroup): void {
    const subscriptions: FormArray = group.get('subscriptions') as FormArray;
    const { issueDate: issueDateControl } =
      this.form.get('summary.keyDates').value;

    const { value: issueDateValue } = issueDateControl;

    const issueDate = DateUtils.convertNgbDateToDayJs(issueDateValue);
    DateUtils.resetClock(issueDate);

    if (!issueDate) {
      return;
    }

    subscriptions.controls.forEach((subscription: FormGroup, index: number) => {
      if (index) {
        subscription
          .get('distributionChannel')
          .setValue(index === 1 ? 'door to door' : 'distance marketing');
      }

      const { distributionChannel } = subscription.value;
      let subStartDate: any = issueDate.add(-21, 'days');
      let subEndDate: any = issueDate.add(-2, 'days');

      if (distributionChannel === 'door to door') {
        subEndDate = issueDate.add(-7, 'days');
      } else if (distributionChannel === 'distance marketing') {
        subEndDate = issueDate.add(-14, 'days');
      }

      subStartDate = DateUtils.convertDayJSDateTimeToNgbDateTime(subStartDate);
      if (subEndDate) {
        subEndDate = DateUtils.convertDayJSDateTimeToNgbDateTime(subEndDate);
      }

      const { subStartDate: subStartDateCtrl, subEndDate: subEndDateCtrl } =
        subscription.controls;

      if (
        !subStartDateCtrl.touched &&
        (!subStartDateCtrl.value || !this.isProductEditing())
      ) {
        subStartDateCtrl.setValue(subStartDate);
      }

      if (
        !subEndDateCtrl.touched &&
        (!subEndDateCtrl.value || !this.isProductEditing())
      ) {
        subEndDateCtrl.setValue(subEndDate);
      }
    });
  }

  private observeForItalianCLNs(): void {
    combineLatest([
      this.form
        .get(ProductCaptureGroupsEnum.legalForm)
        .get(LegalFormEnum.legal)
        .get(LegalEnum.legalFormDropdown)
        .valueChanges.pipe(startWith(null as any)),
      this.form
        .get(ProductCaptureGroupsEnum.legalForm)
        .get(LegalFormEnum.countriesOfOffer)
        .get(LegalFormEnum.countriesOfOfferArr)
        .valueChanges.pipe(startWith([] as any)),
      this.getListingVenueOptions(),
    ])
      .pipe(this.commonPipe())
      .subscribe(
        ([legalForm, countriesOfOfferArr, listingVenues]: [any, any, any]) => {
          const isItalianCLN = countriesOfOfferArr.some(
            (country: { jurisdictionsLegal: string }) => {
              return country.jurisdictionsLegal === 'Italy';
            }
          );
          const isCertificate = legalForm === LEGAL_FORM_KEYS.CERTIFICATE;

          if (!isItalianCLN || !isCertificate) {
            return;
          }

          const listingVenueControl = this.form
            .get(ProductCaptureGroupsEnum.legalForm)
            .get(LegalFormEnum.legal)
            .get(LegalEnum.listingVenue);

          let selectedVenues = listingVenueControl.value ?? [];

          const euroTlxVenue = listingVenues.find(
            (venue: ListingVenue) =>
              venue.shortName === 'ETLX' || venue.micCode === 'ETLX'
          );

          if (!euroTlxVenue) {
            console.warn(
              'ETLX venue not found in listing venues, hence we cannot add it to the listing venues'
            );
            return;
          }

          if (!this.hasAddedETLX) {
            selectedVenues = [...new Set([...selectedVenues, euroTlxVenue.id])];
            this.hasAddedETLX = true;
          } else if (
            this.hasAddedETLX &&
            !selectedVenues.includes(euroTlxVenue.id)
          ) {
            this.hasRemovedETLX = true;
          }

          if (
            !this.hasRemovedETLX &&
            !selectedVenues.includes(euroTlxVenue.id)
          ) {
            selectedVenues.push(euroTlxVenue.id);

            const listingStatusControl = this.form
              .get(ProductCaptureGroupsEnum.legalForm)
              .get(LegalFormEnum.legal)
              .get(LegalEnum.listingStatus);

            if (
              listingStatusControl &&
              listingStatusControl.value !== LISTING_STATUS_KEYS.LISTED
            ) {
              listingStatusControl.setValue(LISTING_STATUS_KEYS.LISTED);
            }
          }

          listingVenueControl.setValue(selectedVenues);
        }
      );
  }

  get productType(): ProductTypeEnum {
    return this.form?.get('summary.productType').value as ProductTypeEnum;
  }
}
